// frontend/src/components/notifications/NotificationCenter.js
import React, { useState, useMemo } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  Chip,
  Button,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Divider,
  Stack,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import InfoIcon from '@mui/icons-material/Info';
import CircleIcon from '@mui/icons-material/Circle';

const MOCK_NOTIFICATIONS = [
  {
    id: '1',
    title: 'AI Replenishment Recommendation',
    message: 'Auto‑order 50 units of Pixel 7 to avoid stockout.',
    type: 'AI_INSIGHT',
    priority: 'HIGH',
    timestamp: '5m ago',
    read: false,
    link: '/ai-insights',
  },
  {
    id: '2',
    title: 'Low Stock Alert',
    message: 'Pixel 7 stock dropped below 10 units.',
    type: 'LOW_STOCK',
    priority: 'MEDIUM',
    timestamp: '12m ago',
    read: false,
    link: '/inventory',
  },
  {
    id: '3',
    title: 'Purchase Order Pending',
    message: 'Draft PO #342 needs your approval.',
    type: 'PO_APPROVAL',
    priority: 'HIGH',
    timestamp: '1h ago',
    read: true,
    link: '/supply-chain',
  },
];

const typeConfig = {
  AI_INSIGHT: {
    icon: <AutoAwesomeIcon />,
    color: 'primary.main',
    label: 'AI Insights',
  },
  LOW_STOCK: {
    icon: <WarningAmberIcon />,
    color: 'warning.main',
    label: 'Low Stock',
  },
  PO_APPROVAL: {
    icon: <ReceiptLongIcon />,
    color: 'success.main',
    label: 'PO Approval',
  },
  SYSTEM: {
    icon: <InfoIcon />,
    color: 'info.main',
    label: 'System',
  },
};

export default function NotificationCenter() {
  const theme = useTheme();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState(MOCK_NOTIFICATIONS);
  const [filter, setFilter] = useState('ALL');

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );

  const filteredNotifications = useMemo(() => {
    if (filter === 'ALL') return notifications;
    if (filter === 'AI') return notifications.filter((n) => n.type === 'AI_INSIGHT');
    if (filter === 'INV_PO')
      return notifications.filter(
        (n) => n.type === 'LOW_STOCK' || n.type === 'PO_APPROVAL'
      );
    return notifications;
  }, [notifications, filter]);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);
  const id = open ? 'notification-popover' : undefined;

  const handleMarkAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const handleDismiss = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const handleView = (notification) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
    );
    navigate(notification.link);
    handleClose();
  };

  const handleClearAll = () => {
    setNotifications([]);
  };

  const handleFilterChange = (event, newValue) => {
    setFilter(newValue);
  };

  const getIcon = (type) => {
    const cfg = typeConfig[type] || typeConfig.SYSTEM;
    return (
      <Avatar
        sx={{
          bgcolor: theme.palette.mode === 'dark' ? `${cfg.color}80` : cfg.color,
          width: 32,
          height: 32,
        }}
      >
        {cfg.icon}
      </Avatar>
    );
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen}>
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 500,
            p: 2,
            bgcolor: theme.palette.background.paper,
          },
        }}
      >
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="h6">Notifications</Typography>
          <Chip
            label={`${unreadCount} Unread`}
            color="error"
            size="small"
            sx={{ ml: 1 }}
          />
          <Button
            size="small"
            onClick={handleMarkAllAsRead}
            disabled={unreadCount === 0}
          >
            Mark all as read
          </Button>
        </Box>

        {/* Filter Tabs */}
        <Tabs
          value={filter}
          onChange={handleFilterChange}
          variant="fullWidth"
          sx={{ mb: 1 }}
        >
          <Tab value="ALL" label="All" />
          <Tab value="AI" label="AI Insights" />
          <Tab value="INV_PO" label="Inventory & POs" />
        </Tabs>

        <Divider />

        {/* Notification List */}
        {filteredNotifications.length === 0 ? (
          <Box textAlign="center" py={4}>
            <InfoIcon fontSize="large" color="action" />
            <Typography variant="subtitle1" mt={1}>
              You're all caught up!
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0 }}>
            {filteredNotifications.map((n) => (
              <React.Fragment key={n.id}>
                <ListItem alignItems="flex-start" sx={{ py: 1 }}>
                  <ListItemAvatar>{getIcon(n.type)}</ListItemAvatar>
                  <ListItemText
                    secondaryTypographyProps={{ component: 'div' }}
                    primary={
                      <Box display="flex" alignItems="center">
                        <Typography
                          variant="subtitle2"
                          fontWeight={n.read ? 'regular' : 'bold'}
                        >
                          {n.title}
                        </Typography>
                        {!n.read && (
                          <Tooltip title="Unread">
                            <CircleIcon
                              fontSize="small"
                              color="error"
                              sx={{ ml: 0.5 }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    }
                    secondary={
                      <Box component="div">
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                          component="div"
                        >
                          {n.message}
                        </Typography>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mt={0.5}
                        >
                          <Typography variant="caption" color="text.disabled">
                            {n.timestamp}
                          </Typography>
                          <Stack direction="row" spacing={0.5}>
                            <Button
                              size="small"
                              onClick={() => handleView(n)}
                            >
                              View
                            </Button>
                            <Button
                              size="small"
                              color="inherit"
                              onClick={() => handleDismiss(n.id)}
                            >
                              Dismiss
                            </Button>
                          </Stack>
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
        )}

        {/* Footer */}
        {notifications.length > 0 && (
          <Box display="flex" justifyContent="center" mt={1}>
            <Button size="small" onClick={handleClearAll}>
              Clear all
            </Button>
          </Box>
        )}
      </Popover>
    </>
  );
}